import { Editable, EnqueteEditor } from "@bleu/nbsr-editor";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { FunctionComponent, useCallback, useState } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";

import { ServerEndpoint } from "../../../../../../server/router/http/endpoint";
import i18n from "../../../../lib/i18n";
import { uploadImage, uploadVideo } from "../../../../lib/queries";
import { upsertTemplate } from "../../../../lib/queries/template";
import { Template } from "../../../../types";
import { Attention } from "../../../parts/Attention";

type Props = {
	template: Editable<Template>;
};

export const TemplateEditorPage: FunctionComponent<Props> = React.memo(({ template, ...props }) => {
	const [t] = useTranslation();

	const [_template, setTemplate] = useState(template);

	const queryClient = useQueryClient();

	const { mutateAsync: mutateTemplate } = useMutation({
		mutationFn: async (template: Editable<Template>) =>
			upsertTemplate(template).then((res) => {
				const { state, value } = res.payload;
				if (state === "loaded") {
					queryClient.setQueryData(["template", template._id], value);
					setTemplate(value);
				}
				return res;
			}),
	});

	const handleSave = useCallback(
		async (template: Editable<Template>) => {
			return await mutateTemplate(template);
		},
		[mutateTemplate]
	);

	const handleUploadImage = useCallback(
		async (file: File) => {
			if (template._id == null)
				return { error: { message: t("temnplateEditorPage.templateIdIsUndefined") }, state: "failed" as const };

			return await uploadImage(`${ServerEndpoint.internal.image.base}/template/${template._id}`, file);
		},
		[template._id]
	);

	const { mutateAsync: handleUploadVideo } = useMutation({
		mutationFn: uploadVideo,
	});

	return (
		<div className="template-editor-page">
			<EnqueteEditor
				job={_template}
				variants={{
					controlSecondary: "outline-primary",
				}}
				webEndpoints={{
					debug: ServerEndpoint.internal.redirect.templateDebugBase,
					enquete: ServerEndpoint.internal.redirect.templateDebugBase,
					preview: ServerEndpoint.internal.redirect.templatePreviewBase,
				}}
				onSave={handleSave}
				onUpdate={setTemplate}
				onUploadImage={handleUploadImage}
				onUploadVideo={handleUploadVideo}
				{...props}
			>
				<I18nextProvider i18n={i18n}>
					<Attention />
				</I18nextProvider>
			</EnqueteEditor>
		</div>
	);
});
